import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, ChevronRight, ArrowLeft, MapPin, Building2, Home } from 'lucide-react';
import api from '../api/axios';
import PageHeader from '../components/layout/PageHeader';
import ConfirmDialog from '../components/shared/ConfirmDialog';

export default function Assemblies() {
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', district: '', pc: '' });

  // Drill-down state
  const [selectedAC, setSelectedAC] = useState(null);
  const [mandals, setMandals] = useState([]);
  const [mandalsLoading, setMandalsLoading] = useState(false);
  const [selectedMandal, setSelectedMandal] = useState(null);
  const [villages, setVillages] = useState([]);
  const [villagesLoading, setVillagesLoading] = useState(false);

  const fetchAssemblies = () => {
    api.get('/assemblies').then(res => setAssemblies(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAssemblies(); }, []);

  // Fetch mandals when an AC is selected
  const openAC = (ac) => {
    setSelectedAC(ac);
    setSelectedMandal(null);
    setVillages([]);
    setMandalsLoading(true);
    api.get(`/assemblies/${ac.id}/mandals`)
      .then(res => setMandals(res.data))
      .catch(() => setMandals([]))
      .finally(() => setMandalsLoading(false));
  };

  // Fetch villages when a mandal is selected
  const openMandal = (mandal) => {
    setSelectedMandal(mandal);
    setVillagesLoading(true);
    api.get(`/assemblies/${selectedAC.id}/mandals/${encodeURIComponent(mandal.mandal)}/villages`)
      .then(res => setVillages(res.data))
      .catch(() => setVillages([]))
      .finally(() => setVillagesLoading(false));
  };

  const goBackToACs = () => { setSelectedAC(null); setSelectedMandal(null); setMandals([]); setVillages([]); };
  const goBackToMandals = () => { setSelectedMandal(null); setVillages([]); };

  const openAdd = () => { setForm({ name: '', code: '', district: '', pc: '' }); setModal('add'); };
  const openEdit = (e, a) => { e.stopPropagation(); setForm({ name: a.name, code: a.code, district: a.district, pc: a.pc }); setModal(a); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modal === 'add') {
        await api.post('/assemblies', form);
      } else {
        await api.put(`/assemblies/${modal.id}`, form);
      }
      setModal(null);
      fetchAssemblies();
    } catch (err) {
      alert(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assemblies/${deleteId}`);
      setDeleteId(null);
      fetchAssemblies();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>;

  const CountBadges = ({ row }) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">{row.total}</span>
      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{row.submitted} sub</span>
      <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700">{row.approved} app</span>
      <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700">{row.rejected} rej</span>
    </div>
  );

  // --- VILLAGES VIEW ---
  if (selectedAC && selectedMandal) {
    return (
      <div>
        <PageHeader title="Villages / Panchayats" subtitle={`${selectedAC.name} (${selectedAC.code}) → ${selectedMandal.mandal}`} />
        <button onClick={goBackToMandals} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Mandals
        </button>

        {/* Mandal summary */}
        <div className="bg-gradient-to-r from-teal-500 to-blue-600 rounded-xl p-5 text-white mb-4">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-6 h-6" />
            <h3 className="text-lg font-bold">{selectedMandal.mandal}</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <div><div className="text-white/60 text-xs">Total</div><div className="text-xl font-bold">{selectedMandal.total}</div></div>
            <div><div className="text-white/60 text-xs">Submitted</div><div className="text-xl font-bold">{selectedMandal.submitted}</div></div>
            <div><div className="text-white/60 text-xs">Approved</div><div className="text-xl font-bold">{selectedMandal.approved}</div></div>
            <div><div className="text-white/60 text-xs">Rejected</div><div className="text-xl font-bold">{selectedMandal.rejected}</div></div>
          </div>
        </div>

        {villagesLoading ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>
        ) : villages.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No villages found</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {villages.map(v => (
              <div key={v.panchayat} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Home className="w-4 h-4 text-teal-500" />
                  <h4 className="font-semibold text-gray-900">{v.panchayat}</h4>
                </div>
                <CountBadges row={v} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- MANDALS VIEW ---
  if (selectedAC) {
    return (
      <div>
        <PageHeader title="Mandals" subtitle={`${selectedAC.name} (${selectedAC.code}) — ${selectedAC.district} District`} />
        <button onClick={goBackToACs} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Assemblies
        </button>

        {/* AC summary */}
        <div className="bg-gradient-to-r from-[#1e293b] via-[#1e40af] to-[#14b8a6] rounded-xl p-5 text-white mb-4">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6" />
            <div>
              <h3 className="text-lg font-bold">{selectedAC.name} ({selectedAC.code})</h3>
              <p className="text-white/60 text-sm">{selectedAC.district} — PC: {selectedAC.pc}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
            <div><div className="text-white/60 text-xs">Total Registrations</div><div className="text-xl font-bold">{selectedAC.total_registrations}</div></div>
            <div><div className="text-white/60 text-xs">Mandals</div><div className="text-xl font-bold">{mandals.length}</div></div>
            <div><div className="text-white/60 text-xs">Admins</div><div className="text-xl font-bold">{selectedAC.admin_count}</div></div>
          </div>
        </div>

        {mandalsLoading ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>
        ) : mandals.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No mandals found</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {mandals.map(m => (
              <div
                key={m.mandal}
                onClick={() => openMandal(m)}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 cursor-pointer hover:shadow-md hover:border-teal-200 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-teal-500" />
                    <h4 className="font-semibold text-gray-900">{m.mandal}</h4>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
                <CountBadges row={m} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- ASSEMBLIES LIST VIEW ---
  return (
    <div>
      <PageHeader title="Assemblies" subtitle="Manage Assembly Constituencies">
        <button onClick={openAdd} className="flex items-center gap-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">
          <Plus className="w-4 h-4" /> Add Assembly
        </button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {assemblies.map(a => (
          <div
            key={a.id}
            onClick={() => openAC(a)}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-teal-200 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1e40af] to-[#14b8a6] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{a.code}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{a.name}</h4>
                  <p className="text-xs text-gray-500">{a.district} — PC: {a.pc}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 mt-1" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{a.total_registrations} registrations</span>
              <span className="text-gray-400 text-xs">{a.admin_count} admin(s)</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              <button onClick={(e) => openEdit(e, a)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); setDeleteId(a.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{modal === 'add' ? 'Add Assembly' : 'Edit Assembly'}</h3>
              <button onClick={() => setModal(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Name" required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder="Code (e.g. 86)" required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={form.district} onChange={e => setForm({...form, district: e.target.value})} placeholder="District" required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={form.pc} onChange={e => setForm({...form, pc: e.target.value})} placeholder="Parliamentary Constituency" required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Assembly"
        message="Are you sure? This will delete all associated registrations."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
